import find from "lodash/find";
import { Select } from "PFComponents/select";
import { Id, Option } from "PFTypes";
import { Phase } from "PFTypes/phase";
import { RefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { usePhases } from "../../../../hooks/queries/phases/use_phases";
import { PHASES_ERROR_KEY } from "../../constants";
import { getFieldError } from "../../errors_helper";

type PhasesSelectProps = {
  className?: string;
  selectedPhaseSourceId?: string;
  activityId?: Id;
  onChange: (phase: Phase) => void;
  errors?: Record<string, string>;
  portalRef?: RefObject<HTMLDivElement | null>;
  label?: string;
  isDisabled?: boolean;
};

export const PhasesSelect = ({
  className,
  selectedPhaseSourceId,
  activityId,
  onChange,
  errors = {},
  portalRef,
  label,
  isDisabled
}: PhasesSelectProps) => {
  const { t } = useTranslation();
  const { data: phases } = usePhases(activityId);

  const phasesOptions: Option<Phase>[] = useMemo(
    () =>
      phases?.entries.map((phase) => ({
        id: phase.sourceId,
        value: phase.name,
        displayElement: phase.name,
        original: phase
      })) || [],
    [phases]
  );

  const handlePhaseChange = (option: Option<Phase> | Option<Phase>[]) => {
    const selected = option as Option<Phase>;
    const selectedPhase = find(phases?.entries, ["sourceId", selected.original.sourceId]) as Phase;

    if (!selectedPhase) {
      return;
    }

    onChange(selectedPhase);
  };

  return (
    <Select
      className={className}
      label={label}
      placeholder={t("availabilityRequirement.phasesSelectPlaceholder")}
      options={phasesOptions}
      disabled={!phasesOptions.length || isDisabled}
      value={find(phasesOptions, ["id", selectedPhaseSourceId])?.id}
      onChange={handlePhaseChange}
      error={getFieldError(errors, PHASES_ERROR_KEY)}
      portalRef={portalRef}
      required
    />
  );
};
